import { FilePlus2, ListChecks } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Definition } from '@survey/core';
import { api, ApiError } from '../api/client';
import { Topbar } from '../components/Layout';
import { Field } from '../components/ui';
import { useI18n, type MessageKey } from '../i18n';

/**
 * A small starter so creators can see logic and randomization working
 * immediately. Its text is created in the creator's current language.
 */
function starter(t: (k: MessageKey) => string): Definition {
  return {
    settings: { randomizeQuestions: false, showProgress: true, allowBack: true },
    welcomeMessage: t('starter.welcome'),
    thankYouMessage: t('starter.thanks'),
    questions: [
      {
        id: 'satisfaction',
        type: 'rating',
        title: t('starter.q1'),
        required: true,
        scale: { min: 1, max: 5, minLabel: t('starter.q1_min'), maxLabel: t('starter.q1_max') },
      },
      {
        id: 'improve',
        type: 'long_text',
        title: t('starter.q2'),
        required: false,
        visibleIf: { match: 'all', conditions: [{ questionId: 'satisfaction', operator: 'lte', value: 3 }] },
      },
      {
        id: 'channels',
        type: 'multiple_choice',
        title: t('starter.q3'),
        required: false,
        randomizeOptions: true,
        options: [
          { id: 'search', label: t('starter.q3_o1') },
          { id: 'friend', label: t('starter.q3_o2') },
          { id: 'social', label: t('starter.q3_o3') },
          { id: 'other', label: t('starter.q3_o4'), fixed: true },
        ],
      },
      { id: 'recommend', type: 'yes_no', title: t('starter.q4'), required: true },
    ],
  };
}

export function NewSurveyPage() {
  const nav = useNavigate();
  const { t } = useI18n();
  const [title, setTitle] = useState('');
  const [template, setTemplate] = useState<'blank' | 'starter'>('blank');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const s = await api.createSurvey({ title, definition: template === 'starter' ? starter(t) : undefined });
      nav(`/surveys/${s.id}`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('new.failed'));
      setBusy(false);
    }
  }

  return (
    <>
      <Topbar title={t('new.title')} crumbs={[{ to: '/surveys', label: t('nav.questionnaires') }]} />
      <form className="card card-pad" style={{ maxWidth: 640 }} onSubmit={submit}>
        <div className="form-grid">
          {error && <div className="banner error">{error}</div>}
          <Field label={t('new.title_label')} htmlFor="title" hint={t('new.title_hint')}>
            <input id="title" className="input" autoFocus maxLength={200} placeholder={t('new.title_placeholder')} value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <div className="field">
            <span className="label">{t('new.start_from')}</span>
            <div className="grid cols-2">
              <TemplateCard active={template === 'blank'} onClick={() => setTemplate('blank')} icon={<FilePlus2 />} title={t('new.blank')} text={t('new.blank_text')} />
              <TemplateCard active={template === 'starter'} onClick={() => setTemplate('starter')} icon={<ListChecks />} title={t('new.starter')} text={t('new.starter_text')} />
            </div>
          </div>
          <div className="btn-row">
            <button className="btn primary" disabled={busy}>
              {busy ? t('auth.creating') : t('new.create')}
            </button>
          </div>
        </div>
      </form>
    </>
  );
}

function TemplateCard({ active, onClick, icon, title, text }: { active: boolean; onClick: () => void; icon: React.ReactNode; title: string; text: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="card"
      style={{
        textAlign: 'left',
        padding: 16,
        cursor: 'pointer',
        borderColor: active ? 'var(--accent-strong)' : undefined,
        boxShadow: active ? 'var(--focus)' : undefined,
      }}
    >
      <div className="row gap-12">
        <div className="tile">{icon}</div>
        <div>
          <div style={{ fontWeight: 500 }}>{title}</div>
          <div className="small muted">{text}</div>
        </div>
      </div>
    </button>
  );
}
